import React, { useCallback } from "react";

import { MultipleSelectOrderedInput, SortableOrderedList } from "@performyard/py-components";
import type { DragHandlers } from "@performyard/py-components/src/components/molecules/SortableOrderedList";
import type { Option } from "@performyard/py-components/src/types/Option";

import { fetchEmployees } from "../../../api/services";

import {
	isStaticEmployeeOption,
	renderEmployeeDropdownOption,
	renderEmployeeOrderedItem,
	renderEmployeeOrderedItemReadOnly,
} from "../../FormSelectionCard/utils/employeeSelectionUtils";

import { EmployeeValue } from "./shared";

export type MultipleOrderedSelectEmployeeProps = {
	/**
	 * Static options to prepend to fetched results (e.g., "Employee Being Reviewed")
	 * Defaults to [] (no static options). Pass STATIC_EMPLOYEE_OPTIONS from FormSelectionCard utils if needed.
	 */
	staticOptions?: Option<EmployeeValue>[];
	/**
	 * Optional custom fetch function. If not provided, uses default employee search
	 * with staticOptions prepended.
	 */
	fetchOptions?: (searchTerm: string) => Promise<Option<EmployeeValue>[]>;
	/**
	 * Currently selected values
	 */
	value: EmployeeValue[];
	/**
	 * Callback when selection changes
	 */
	onChange: (employees: EmployeeValue[]) => void;
	/**
	 * Whether the input is in an error state
	 */
	errored?: boolean;
	/**
	 * Whether the input is disabled
	 */
	disabled?: boolean;
	/**
	 * Whether the input is read-only (no drag handles, no dismiss buttons)
	 */
	readOnly?: boolean;
	/**
	 * Whether to close the dropdown after each selection in multiple mode.
	 * @default false
	 */
	closeOnSelect?: boolean;
	/**
	 * Placeholder text
	 */
	placeholder?: string;
	/**
	 * Input ID
	 */
	id?: string;
	/**
	 * Test ID
	 */
	"data-testid"?: string;
	/**
	 * Optional CSS classes
	 */
	dangerouslySetClassName?: string;
	/**
	 * Custom render function for ordered items
	 */
	renderItem?: (
		value: EmployeeValue,
		index: number,
		onRemove: () => void,
		dragHandlers: DragHandlers,
		isDragging: boolean
	) => React.ReactNode;
	/**
	 * Custom render function for dropdown options
	 */
	renderOption?: (option: Option<EmployeeValue>, isSelected: boolean) => React.ReactNode;
};

export const MultipleOrdered = ({
	staticOptions = [],
	fetchOptions: customFetchOptions,
	value,
	onChange,
	errored,
	disabled,
	readOnly,
	closeOnSelect,
	placeholder = "Search for employees...",
	id,
	"data-testid": dataTestId,
	dangerouslySetClassName,
	renderItem: customRenderItem,
	renderOption: customRenderOption,
}: MultipleOrderedSelectEmployeeProps) => {
	const getItemId = useCallback((employee: EmployeeValue) => employee.id, []);

	// Use custom render functions if provided, otherwise use defaults
	const renderItem = customRenderItem ?? (readOnly ? renderEmployeeOrderedItemReadOnly : renderEmployeeOrderedItem);
	const renderOption = customRenderOption ?? renderEmployeeDropdownOption;

	// Default fetch function that combines static options with employee search
	const defaultFetchOptions = useCallback(
		async (searchTerm: string): Promise<Option<EmployeeValue>[]> => {
			try {
				// Filter static options based on search term
				const filteredStaticOptions = staticOptions.filter((option) =>
					String(option.label).toLowerCase().includes(searchTerm.toLowerCase())
				);

				const data = await fetchEmployees({ searchTerm });
				const employeeOptions: Option<EmployeeValue>[] =
					data.users?.map((user) => ({
						label: user.name,
						value: {
							id: user._id,
							name: user.name,
						},
					})) || [];

				return [...filteredStaticOptions, ...employeeOptions];
			} catch (error) {
				console.error("Error fetching employees:", error);
				// Return filtered static options on error
				return staticOptions.filter((option) =>
					String(option.label).toLowerCase().includes(searchTerm.toLowerCase())
				);
			}
		},
		[staticOptions]
	);

	const fetchOptions = customFetchOptions ?? defaultFetchOptions;

	// Placeholders stand for a role, not a person, and templates legitimately assign the same role
	// more than once, so they stay selectable. Named employees still drop out once chosen.
	const allowDuplicateOption = useCallback(
		(option: Option<EmployeeValue>) => isStaticEmployeeOption(option.value.id),
		[]
	);

	if (readOnly) {
		return (
			<SortableOrderedList<EmployeeValue>
				values={value}
				readOnly
				renderItem={renderItem}
				getItemId={getItemId}
				dangerouslySetClassName={dangerouslySetClassName}
			/>
		);
	}

	return (
		<MultipleSelectOrderedInput<EmployeeValue>
			fetchOptions={fetchOptions}
			value={value}
			onChange={onChange}
			selectionVariant="combobox"
			closeOnSelect={closeOnSelect}
			placeholder={placeholder}
			errored={errored}
			disabled={disabled}
			id={id}
			data-testid={dataTestId}
			renderOrderedItem={renderItem}
			renderOption={renderOption}
			allowDuplicateOption={allowDuplicateOption}
			dangerouslySetClassName={dangerouslySetClassName}
		/>
	);
};

MultipleOrdered.displayName = "MultipleOrderedSelectEmployee";
